import React, { useState, useEffect, useMemo } from "react";
import { Formik, Form, FieldArray, getIn } from "formik";
import * as Yup from "yup";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
  Backdrop,
  Grid,
  Stack,
  Checkbox,
  Card,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { eopdAPI } from "../../../../../services/e-opd.api";
import { toast } from "react-toastify";

// Styles
import "../OpdStepper.css";

export const drugsValidationSchema = Yup.object().shape({
  drugs: Yup.array().of(
    Yup.object().shape({
      drug: Yup.object().nullable(),
      dosage: Yup.string().trim().when("drug", {
        is: (val) => val !== null && val !== undefined,
        then: (s) => s.required("Dosage is required"),
        otherwise: (s) => s.notRequired(),
      }),
      frequency: Yup.string().trim().when("drug", {
        is: (val) => val !== null && val !== undefined,
        then: (s) => s.required("Frequency is required"),
        otherwise: (s) => s.notRequired(),
      }),
      duration: Yup.string().trim().when("drug", {
        is: (val) => val !== null && val !== undefined,
        then: (s) => s.required("Duration is required"),
        otherwise: (s) => s.notRequired(),
      }),
      splInstruct: Yup.string().trim().max(200, "Cannot exceed 200 characters"),
    })
  ),
});

function DrugAutocomplete({ value, onChange, error, helperText, isSubmitting }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");

  // Sync inputValue with selection
  useEffect(() => {
    setInputValue(value?.medicine_name || "");
  }, [value]);

  useEffect(() => {
    if (inputValue.trim().length < 3) {
      setOptions([]);
      return;
    }

    if (value && value.medicine_name === inputValue) {
      return;
    }

    const delayDebounce = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await eopdAPI.searchDrugs(inputValue);
        if (res && res.success && Array.isArray(res.drugs)) {
          setOptions(res.drugs);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [inputValue, value]);

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      getOptionLabel={(option) => option.medicine_name || ""}
      isOptionEqualToValue={(option, val) => option.id === val?.id}
      loading={loading}
      value={value || null}
      onChange={(event, newValue) => {
        onChange(newValue);
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      disabled={isSubmitting}
      className="opd-input-field"
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder="Enter drug"
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default function Step7DrugsDispensation({
  nextStep,
  prevStep,
  submitLabel = "Next",
  showActions = true,
}) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState({
    drugs: true,
  });
  const [selectedConsultation, setSelectedConsultation] = useState("");
  const [savedDrugs, setSavedDrugs] = useState(null);

  const handleConsultationChange = (type) => {
    setSelectedConsultation((prev) => (prev === type ? "" : type));
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded((prev) => ({ ...prev, [panel]: isExpanded }));
  };

  // Load existing drugs from local payload or API
  useEffect(() => {
    const savedLocal = localStorage.getItem("opdDrugsPayload");
    if (savedLocal) {
      try {
        const parsed = JSON.parse(savedLocal);
        if (Array.isArray(parsed.drugs) && parsed.drugs.length > 0) {
          const mapped = parsed.drugs.map((d) => ({
            drug: d.drug_id ? { id: d.drug_id, drug_name: d.medicine_name || `Drug #${d.drug_id}` } : null,
            dosage: d.dosage || "",
            frequency: d.frequency || "",
            duration: d.duration || "",
            splInstruct: d.spl_instruct || "",
          }));
          setSavedDrugs(mapped);
          return;
        }
      } catch (e) {
        console.error("Error parsing saved drugs payload:", e);
      }
    }

    const displayId = localStorage.getItem("currentOpdDisplayId") || "";
    if (displayId) {
      const fetchStep7 = async () => {
        try {
          const res = await eopdAPI.getOpdStepData(displayId, 7);
          if (res && res.success && res.data && Array.isArray(res.data.drugs) && res.data.drugs.length > 0) {
            const mapped = res.data.drugs.map((d) => ({
              drug: d.drug_id ? { id: d.drug_id, drug_name: d.medicine_name || `Drug #${d.drug_id}` } : null,
              dosage: d.dosage || "",
              frequency: d.frequency || "",
              duration: d.duration || "",
              splInstruct: d.spl_instruct || "",
            }));
            setSavedDrugs(mapped);
          }
        } catch (err) {
          console.error("Error loading step 7 drugs data:", err);
        }
      };
      fetchStep7();
    }
  }, []);

  const initialFormValues = useMemo(() => ({
    drugs: Array.isArray(savedDrugs) && savedDrugs.length > 0 ? savedDrugs : [{
      drug: null,
      dosage: "",
      frequency: "",
      duration: "",
      splInstruct: "",
    }],
  }), [savedDrugs]);

  return (
    <Formik
      initialValues={initialFormValues}
      validationSchema={drugsValidationSchema}
      enableReinitialize
      onSubmit={async (values, helpers) => {
        try {
          // Filter out rows where drug is not selected
          const activePrescriptions = (values.drugs || []).filter((d) => d.drug !== null && d.drug !== undefined);

          const displayId = localStorage.getItem("currentOpdDisplayId") || "";
          const payload = {
            display_id: displayId,
            drugs: activePrescriptions.map((d) => ({
              drug_id: d.drug?.id,
              dosage: d.dosage.trim(),
              frequency: d.frequency.trim(),
              duration: d.duration.trim(),
              ...(d.splInstruct?.trim() ? { spl_instruct: d.splInstruct.trim() } : {}),
            })),
          };
          // Store the payload in localStorage to be submitted in the final step
          localStorage.setItem("opdDrugsPayload", JSON.stringify(payload));
          toast.success("e-Consultation & Drugs Dispensation details saved locally.");
          if (nextStep) {
            nextStep();
          } else {
            navigate(`/e-opd/visit-summary/${displayId}`);
          }
        } catch (error) {
          console.error(error);
          toast.error("Failed to save details.");
        } finally {
          helpers.setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, setFieldValue, handleBlur, isSubmitting }) => {
        const rows = values.drugs || [];

        return (
          <Form noValidate>
            <Backdrop
              sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={isSubmitting}
            >
              <CircularProgress color="inherit" />
            </Backdrop>

            <Typography className="opd-step-title">
              e-Consultation & Drugs Dispensation
            </Typography>

            {/* e-Consultation Section */}
            <Card
              elevation={0}
              sx={{
                border: "1px solid #D8DFEA",
                borderRadius: "12px",
                p: 3,
                mb: 3,
                backgroundColor: "#FFFFFF",
              }}
            >
              <Typography
                sx={{
                  fontSize: "15px",
                  fontWeight: 700,
                  color: "#111C33",
                  mb: 2,
                }}
              >
                e-Consultation
              </Typography>

              <Grid container spacing={2}>
                {/* eSanjeevani Option */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      p: 2,
                      border: selectedConsultation === "eSanjeevani" ? "1.5px solid #1E88E5" : "1.5px solid #E5E7EB",
                      borderRadius: "8px",
                      backgroundColor: selectedConsultation === "eSanjeevani" ? "#F4F9FF" : "#FFFFFF",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, flex: 1 }}>
                      <Checkbox
                        checked={selectedConsultation === "eSanjeevani"}
                        onChange={() => handleConsultationChange("eSanjeevani")}
                        size="small"
                        sx={{
                          color: selectedConsultation === "eSanjeevani" ? "#1E88E5 !important" : "#9CA3AF !important",
                          "&.Mui-checked": {
                            color: "#1E88E5 !important",
                          },
                          p: 0,
                          flexShrink: 0,
                        }}
                      />
                      <img
                        src="/images/e-sanjeevani.svg"
                        alt="eSanjeevani"
                        style={{ height: "48px", width: "auto", flexShrink: 0, display: "block" }}
                      />
                    </Stack>
                    
                    {selectedConsultation === "eSanjeevani" && (
                      <Button
                        variant="contained"
                        disableElevation
                        size="small"
                        onClick={() => toast.info("Coming soon")}
                        sx={{
                          backgroundColor: "#1E88E5",
                          fontWeight: 700,
                          fontSize: "12px",
                          textTransform: "none",
                          borderRadius: "20px",
                          px: 2.5,
                          py: 0.5,
                          flexShrink: 0,
                          "&:hover": { backgroundColor: "#1565C0" },
                        }}
                      >
                        Start
                      </Button>
                    )}
                  </Box>
                </Grid>

                {/* Tele-MANAS Option */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      p: 2,
                      border: selectedConsultation === "TeleManas" ? "1.5px solid #1E88E5" : "1.5px solid #E5E7EB",
                      borderRadius: "8px",
                      backgroundColor: selectedConsultation === "TeleManas" ? "#F4F9FF" : "#FFFFFF",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, flex: 1 }}>
                      <Checkbox
                        checked={selectedConsultation === "TeleManas"}
                        onChange={() => handleConsultationChange("TeleManas")}
                        size="small"
                        sx={{
                          color: selectedConsultation === "TeleManas" ? "#1E88E5 !important" : "#9CA3AF !important",
                          "&.Mui-checked": {
                            color: "#1E88E5 !important",
                          },
                          p: 0,
                          flexShrink: 0,
                        }}
                      />
                      <img
                        src="/images/tele-manas.svg"
                        alt="Tele-MANAS"
                        style={{ height: "48px", width: "auto", flexShrink: 0, display: "block" }}
                      />
                    </Stack>
                    
                    {selectedConsultation === "TeleManas" && (
                      <Button
                        variant="contained"
                        disableElevation
                        size="small"
                        onClick={() => toast.info("Coming soon")}
                        sx={{
                          backgroundColor: "#1E88E5",
                          fontWeight: 700,
                          fontSize: "12px",
                          textTransform: "none",
                          borderRadius: "20px",
                          px: 2.5,
                          py: 0.5,
                          flexShrink: 0,
                          "&:hover": { backgroundColor: "#1565C0" },
                        }}
                      >
                        Start
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Card>

            {/* Accordion Container for Drugs */}
            <Accordion
              expanded={expanded.drugs}
              onChange={handleAccordionChange("drugs")}
              className={`opd-accordion ${expanded.drugs ? "opd-accordion-expanded" : ""}`}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className="opd-accordion-summary">
                <Box>
                  <Typography className="opd-accordion-title">Drugs</Typography>
                  <Typography className="opd-accordion-subtitle">
                    Select from the facility's 113-drug formulary. Add each drug dispensed with dose and instructions.
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails className="opd-accordion-details">
                <FieldArray name="drugs">
                  {({ remove, push }) => (
                    <Stack spacing={3}>
                      {rows.map((row, i) => {
                        const drugErr = getIn(errors, `drugs[${i}].drug`);
                        const drugTouched = getIn(touched, `drugs[${i}].drug`);
                        const hasDrugErr = Boolean(drugTouched && drugErr);

                        const dosageErr = getIn(errors, `drugs[${i}].dosage`);
                        const dosageTouched = getIn(touched, `drugs[${i}].dosage`);
                        const hasDosageErr = Boolean(dosageTouched && dosageErr);

                        const freqErr = getIn(errors, `drugs[${i}].frequency`);
                        const freqTouched = getIn(touched, `drugs[${i}].frequency`);
                        const hasFreqErr = Boolean(freqTouched && freqErr);

                        const durErr = getIn(errors, `drugs[${i}].duration`);
                        const durTouched = getIn(touched, `drugs[${i}].duration`);
                        const hasDurErr = Boolean(durTouched && durErr);

                        const splErr = getIn(errors, `drugs[${i}].splInstruct`);
                        const splTouched = getIn(touched, `drugs[${i}].splInstruct`);
                        const hasSplErr = Boolean(splTouched && splErr);

                        return (
                          <Grid container spacing={2} key={i} alignItems="flex-start">
                            <Grid size={{ xs: 12, md: 2.2 }}>
                              <Box className="opd-input-group">
                                <Typography className="opd-field-label">Drug Name</Typography>
                                <DrugAutocomplete
                                  value={row.drug}
                                  onChange={(newValue) => setFieldValue(`drugs[${i}].drug`, newValue)}
                                  error={hasDrugErr}
                                  helperText={hasDrugErr ? drugErr : ""}
                                  isSubmitting={isSubmitting}
                                />
                              </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 2.2 }}>
                              <Box className="opd-input-group">
                                <Typography className="opd-field-label">Dosage</Typography>
                                <TextField
                                  fullWidth
                                  size="small"
                                  disabled={isSubmitting}
                                  name={`drugs[${i}].dosage`}
                                  value={row.dosage || ""}
                                  onChange={(e) => setFieldValue(`drugs[${i}].dosage`, e.target.value)}
                                  onBlur={handleBlur}
                                  placeholder="Enter dosage"
                                  error={hasDosageErr}
                                  helperText={hasDosageErr ? dosageErr : ""}
                                  className="opd-input-field"
                                />
                              </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 2.2 }}>
                              <Box className="opd-input-group">
                                <Typography className="opd-field-label">Frequency</Typography>
                                <TextField
                                  fullWidth
                                  size="small"
                                  disabled={isSubmitting}
                                  name={`drugs[${i}].frequency`}
                                  value={row.frequency || ""}
                                  onChange={(e) => setFieldValue(`drugs[${i}].frequency`, e.target.value)}
                                  onBlur={handleBlur}
                                  placeholder="Enter frequency"
                                  error={hasFreqErr}
                                  helperText={hasFreqErr ? freqErr : ""}
                                  className="opd-input-field"
                                />
                              </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 2.2 }}>
                              <Box className="opd-input-group">
                                <Typography className="opd-field-label">Duration</Typography>
                                <TextField
                                  fullWidth
                                  size="small"
                                  disabled={isSubmitting}
                                  name={`drugs[${i}].duration`}
                                  value={row.duration || ""}
                                  onChange={(e) => setFieldValue(`drugs[${i}].duration`, e.target.value)}
                                  onBlur={handleBlur}
                                  placeholder="Enter duration"
                                  error={hasDurErr}
                                  helperText={hasDurErr ? durErr : ""}
                                  className="opd-input-field"
                                />
                              </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 2.4 }}>
                              <Box className="opd-input-group">
                                <Typography className="opd-field-label">Special Instruction (Timing)</Typography>
                                <TextField
                                  fullWidth
                                  size="small"
                                  disabled={isSubmitting}
                                  name={`drugs[${i}].splInstruct`}
                                  value={row.splInstruct || ""}
                                  onChange={(e) => setFieldValue(`drugs[${i}].splInstruct`, e.target.value)}
                                  onBlur={handleBlur}
                                  placeholder="Enter instruction"
                                  error={hasSplErr}
                                  helperText={hasSplErr ? splErr : ""}
                                  className="opd-input-field"
                                />
                              </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 0.8 }}>
                              <Box className="opd-delete-wrapper">
                                <Typography className="opd-field-label">&nbsp;</Typography>
                                <IconButton
                                  disabled={isSubmitting}
                                  onClick={() => remove(i)}
                                  className="opd-delete-btn"
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Grid>
                          </Grid>
                        );
                      })}

                      <Box>
                        <Button
                          onClick={() =>
                            push({
                              drug: null,
                              dosage: "",
                              frequency: "",
                              duration: "",
                              splInstruct: "",
                            })
                          }
                          disabled={isSubmitting}
                          startIcon={<AddIcon />}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#1E88E5",
                            backgroundColor: "#E3F2FD",
                            borderRadius: "8px",
                            px: 3,
                            py: 1,
                            "&:hover": { backgroundColor: "#D0E7FF" },
                          }}
                        >
                          Add Drug
                        </Button>
                      </Box>
                    </Stack>
                  )}
                </FieldArray>
              </AccordionDetails>
            </Accordion>

            {showActions && (
              <Stack
                direction="row"
                justifyContent={prevStep ? "space-between" : "flex-end"}
                alignItems="center"
                sx={{ mt: 3 }}
              >
                {prevStep && (
                  <Button
                    type="button"
                    variant="outlined"
                    className="opd-back-btn"
                    onClick={prevStep}
                  >
                    Back
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  disabled={isSubmitting}
                  className="opd-next-btn"
                >
                  {submitLabel}
                </Button>
              </Stack>
            )}
          </Form>
        );
      }}
    </Formik>
  );
}
